import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import { databases } from "../lib/appwrite";

import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { Query } from "appwrite";

const DB_ID = process.env.REACT_APP_APPWRITE_DB_ID;
const COLLECTION_ID = process.env.REACT_APP_APPWRITE_COLLECTION_ID;

const GENERAL_STEPS = [
  "Pending",
  "In Transit",
  "Out for Delivery",
  "Delivered",
];

const STEP_ICONS = {
  Pending: <HourglassEmptyIcon />,
  "In Transit": <LocalShippingIcon />,
  "Out for Delivery": <LocalShippingIcon />,
  Delivered: <CheckCircleIcon />,
  Exception: <WarningIcon />,
  Delayed: <WarningIcon />,
};

export default function TrackingPage() {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [openRows, setOpenRows] = useState({});

  const handleSearch = async () => {
    if (!trackingNumber.trim()) return;
    setLoading(true);
    setMessage("");
    setShipments([]);

    try {
      const res = await databases.listDocuments(DB_ID, COLLECTION_ID, [
        Query.equal("trackingNumber", trackingNumber.trim()),
      ]);

      if (!res.documents.length) {
        setMessage("No shipments found for this tracking number.");
      } else {
        setShipments(res.documents);
      }
    } catch (err) {
      console.error(err);
      setMessage("Unable to retrieve shipment data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const buildSteps = (status) => {
    const steps = [...GENERAL_STEPS];
    if (status === "Exception") steps.splice(1, 0, "Exception");
    if (status === "Delayed") steps.splice(2, 0, "Delayed");
    return steps;
  };

  const getActiveStep = (steps, status) => {
    const index = steps.indexOf(status);
    return index >= 0 ? index : steps.length - 1;
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Shipment Tracking
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Enter your tracking number below to see the status of your shipment.
        </Typography>
      </Paper>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="Tracking Number"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          fullWidth
        />
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
      </Stack>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!loading && message && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {message}
        </Typography>
      )}

      {shipments.length > 0 && (
        <Paper sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Shipment Details
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tracking #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shipments.map((s) => (
                  <TableRow key={s.$id}>
                    <TableCell>{s.trackingNumber}</TableCell>
                    <TableCell>{s.customerName}</TableCell>
                    <TableCell>{s.destinationCity}</TableCell>
                    <TableCell>{s.status}</TableCell>
                    <TableCell>{s.details}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {shipments.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Shipment Progress
          </Typography>
          {shipments.map((s) => {
            const steps = buildSteps(s.status);
            const activeStep = getActiveStep(steps, s.status);

            return (
              <Box key={s.$id} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Tracking #: {s.trackingNumber}
                </Typography>

                {/* Progress bar */}
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((step, index) => (
                    <Step key={step}>
                      <StepLabel
                        StepIconComponent={() =>
                          STEP_ICONS[step] || <HourglassEmptyIcon />
                        }
                        sx={{
                          "& .MuiStepLabel-label": {
                            color:
                              index === activeStep
                                ? "#1976d2"
                                : "text.disabled",
                            fontWeight:
                              index === activeStep ? "bold" : "normal",
                          },
                        }}
                        StepIconProps={{
                          sx: {
                            color:
                              index === activeStep
                                ? "#1976d2"
                                : "text.disabled",
                          },
                        }}
                      >
                        {step}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>

                {/* History Section */}
                <Box sx={{ mt: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    onClick={() =>
                      setOpenRows((prev) => ({
                        ...prev,
                        [s.$id]: !prev[s.$id],
                      }))
                    }
                    sx={{
                      cursor: "pointer",
                      color: "#1976d2",
                      fontWeight: "bold",
                    }}
                  >
                    <Typography variant="subtitle2">View History</Typography>
                    <IconButton size="small">
                      {openRows[s.$id] ? (
                        <KeyboardArrowUpIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowDownIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Stack>

                  <Collapse in={openRows[s.$id]} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 1 }}>
                      <Divider sx={{ mb: 1 }} />
                      <List dense>
                        {[...(s.records || [])]
                          .slice()
                          .reverse()
                          .map((record, idx) => (
                            <ListItem key={idx} sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    fontWeight={idx === 0 ? "bold" : "normal"}
                                  >
                                    {record}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                      </List>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}
    </Container>
  );
}
